import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const URL = "https://coastsidelaw.com.au/shareholder-agreements";

const FAQS = [
  {
    q: "What is a shareholder agreement?",
    a: "A private contract between the owners of a company. It covers how decisions are made, what happens when someone wants to leave, and how shares are valued and sold. It sits alongside the company constitution.",
  },
  {
    q: "Do I need one if we're friends or family?",
    a: "Yes, and arguably more. Most disputes between co-owners come from situations nobody planned for, such as one person stopping work, a marriage breakdown or a death. An agreement settles those questions while everyone still gets along.",
  },
  {
    q: "What happens if a shareholder dies?",
    a: "Without an agreement, their shares pass to their estate. A well-drafted agreement can give the other owners the right, or the obligation, to buy those shares at a set value, often funded by insurance.",
  },
  {
    q: "Can you review an existing agreement?",
    a: "Yes. Older agreements often don't fit the business as it is now. We'll tell you what's missing and what needs updating.",
  },
  {
    q: "How much does a shareholder agreement cost?",
    a: "It depends on the number of owners and how complex the business is. We give you a fixed quote before we start.",
  },
];

export const Route = createFileRoute("/shareholder-agreements")({
  head: () => ({
    meta: [
      { title: fitTitle("Shareholder Agreement Lawyers QLD | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Shareholder agreements drafted and reviewed by Queensland solicitors: decision-making, exits, deadlock, death and share valuation. Fixed quotes, Golden Beach and Scarborough.",
        ),
      },
      { property: "og:title", content: "Shareholder agreements in Queensland" },
      { property: "og:description", content: "Settle the hard questions between business owners before they become disputes." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Business · Co-owners"
      title="Shareholder agreements"
      lead="A shareholder agreement sets the rules between the owners of a company: who decides what, how someone leaves, and what happens to their shares if they die or fall out with the others. We draft new agreements and review existing ones for businesses on the Sunshine Coast, Redcliffe Peninsula and across Queensland, with a fixed quote before we start."
      ctaLabel="Get a fixed quote"
      boxTitle="What a good agreement deals with"
      boxItems={[
        "How decisions are made, and which ones need everyone's agreement",
        "Deadlock: what happens when owners can't agree",
        "Leaving the business, and how shares are valued and sold",
        "Death or loss of capacity, including insurance-funded buyouts",
        "Restraints on competing and taking clients",
        "Bringing in new shareholders or investors",
      ]}
      faqs={FAQS}
      links={[
        { to: "/business-succession-planning", label: "Business succession planning" },
        { to: "/business-sales", label: "Buying & selling a business" },
        { to: "/business-lawyers", label: "Business lawyers" },
      ]}
      formHeading="Ask about a shareholder agreement"
      ctaTitle="Settle it while everyone agrees"
      ctaBody={`Call ${FIRM.phone} for a fixed quote.`}
    />
  ),
});
